export type VertebrateGroup =
  "fish" | "amphibian" | "reptile" | "bird" | "mammal";
export type KeyOutcome =
  | VertebrateGroup
  | "invertebrate-with-exoskeleton"
  | "invertebrate-without-exoskeleton";

export interface AnimalEvidence {
  id: string;
  name: string;
  habitat: "land" | "shore" | "water";
  hasBackbone: boolean;
  hasHardOuterCovering: boolean;
  observableTrait:
    | "fins and gills"
    | "smooth moist skin"
    | "dry scales"
    | "feathers"
    | "fur or hair"
    | "soft body"
    | "hard jointed outer covering";
  disclosedEvidence: string;
  outcome: KeyOutcome;
  transfer?: boolean;
}

export const classificationAnimals: AnimalEvidence[] = [
  {
    id: "salmon",
    name: "salmon",
    habitat: "water",
    hasBackbone: true,
    hasHardOuterCovering: false,
    observableTrait: "fins and gills",
    disclosedEvidence: "Has a backbone, fins, and gills; lives in water.",
    outcome: "fish",
  },
  {
    id: "frog",
    name: "frog",
    habitat: "shore",
    hasBackbone: true,
    hasHardOuterCovering: false,
    observableTrait: "smooth moist skin",
    disclosedEvidence: "Has a backbone and smooth, moist skin.",
    outcome: "amphibian",
  },
  {
    id: "gecko",
    name: "gecko",
    habitat: "land",
    hasBackbone: true,
    hasHardOuterCovering: false,
    observableTrait: "dry scales",
    disclosedEvidence: "Has a backbone and dry, scaly skin.",
    outcome: "reptile",
  },
  {
    id: "puffin",
    name: "puffin",
    habitat: "shore",
    hasBackbone: true,
    hasHardOuterCovering: false,
    observableTrait: "feathers",
    disclosedEvidence: "Has a backbone, wings, and feathers.",
    outcome: "bird",
  },
  {
    id: "seal",
    name: "harbour seal",
    habitat: "shore",
    hasBackbone: true,
    hasHardOuterCovering: false,
    observableTrait: "fur or hair",
    disclosedEvidence: "Has a backbone and short fur; breathes air.",
    outcome: "mammal",
  },
  {
    id: "crab",
    name: "shore crab",
    habitat: "shore",
    hasBackbone: false,
    hasHardOuterCovering: true,
    observableTrait: "hard jointed outer covering",
    disclosedEvidence:
      "Has no backbone; a hard jointed covering supports and protects its outside.",
    outcome: "invertebrate-with-exoskeleton",
  },
  {
    id: "ladybird",
    name: "ladybird beetle",
    habitat: "land",
    hasBackbone: false,
    hasHardOuterCovering: true,
    observableTrait: "hard jointed outer covering",
    disclosedEvidence:
      "Has no backbone and has a hard outer covering with jointed legs.",
    outcome: "invertebrate-with-exoskeleton",
  },
  {
    id: "earthworm",
    name: "earthworm",
    habitat: "land",
    hasBackbone: false,
    hasHardOuterCovering: false,
    observableTrait: "soft body",
    disclosedEvidence: "Has no backbone and no hard outer covering.",
    outcome: "invertebrate-without-exoskeleton",
  },
  {
    id: "octopus",
    name: "octopus",
    habitat: "water",
    hasBackbone: false,
    hasHardOuterCovering: false,
    observableTrait: "soft body",
    disclosedEvidence:
      "Has no backbone and a soft body without a hard outer covering.",
    outcome: "invertebrate-without-exoskeleton",
  },
  {
    id: "newt",
    name: "crested newt",
    habitat: "shore",
    hasBackbone: true,
    hasHardOuterCovering: false,
    observableTrait: "smooth moist skin",
    disclosedEvidence:
      "Unfamiliar visitor: has a backbone and smooth, moist skin; spends time in water and on land.",
    outcome: "amphibian",
    transfer: true,
  },
];

export function resolveIdentificationKey(animal: AnimalEvidence): KeyOutcome {
  if (!animal.hasBackbone) {
    return animal.hasHardOuterCovering
      ? "invertebrate-with-exoskeleton"
      : "invertebrate-without-exoskeleton";
  }
  if (animal.observableTrait === "feathers") return "bird";
  if (animal.observableTrait === "fur or hair") return "mammal";
  if (animal.observableTrait === "fins and gills") return "fish";
  if (animal.observableTrait === "smooth moist skin") return "amphibian";
  if (animal.observableTrait === "dry scales") return "reptile";
  throw new Error(`Incomplete vertebrate evidence for ${animal.id}`);
}

export const keyRouteRepresentatives: Record<KeyOutcome, string> = {
  fish: "salmon",
  amphibian: "frog",
  reptile: "gecko",
  bird: "puffin",
  mammal: "seal",
  "invertebrate-with-exoskeleton": "crab",
  "invertebrate-without-exoskeleton": "octopus",
};
